import { Attachment } from './attachment.model';
import { Permission } from '@ee/common/enums';

/**
 * Client mirror of the server's `AttachmentPermissions`.
 *
 * Document permissions are split by KIND — uploaded vs generated — because the two are different
 * things to a firm: an upload is evidence someone handed over, a generated document is something the
 * product produced from a template. Someone who may correct a generated notice is not automatically
 * someone who may alter a landlord's uploaded lease.
 *
 * These decide what the UI OFFERS. The server decides what actually happens; a client that gets this
 * wrong produces a 403, not an unauthorised change. Keep the two in step anyway — a button that
 * always fails is worse than no button.
 */

/** `true` when the attachment is a product-generated document rather than an upload. */
function isGenerated(attachment: Attachment | undefined): boolean {
  return !!attachment?.generated;
}

export function viewPermissionFor(generated: boolean): Permission {
  return generated ? Permission.CASE_GENERATE_DOCUMENTS_VIEW : Permission.CASE_UPLOAD_DOCUMENTS_VIEW;
}

export function editPermissionFor(generated: boolean): Permission {
  return generated ? Permission.CASE_GENERATE_DOCUMENTS_EDIT : Permission.CASE_UPLOAD_DOCUMENTS_EDIT;
}

export function deletePermissionFor(generated: boolean): Permission {
  return generated ? Permission.CASE_GENERATE_DOCUMENTS_DELETE : Permission.CASE_UPLOAD_DOCUMENTS_DELETE;
}

/**
 * Whether these permissions may see this document.
 *
 * Accepts the pre-split coarse permission, which covered both kinds — every group that predates the
 * split holds it, and honouring it is what makes the split additive rather than a removal of access.
 */
export function canViewAttachment(permissions: string[] | undefined, attachment: Attachment | undefined): boolean {
  const held = permissions ?? [];
  return held.includes(Permission.CASE_DOCUMENTS_VIEW)
    || held.includes(viewPermissionFor(isGenerated(attachment)));
}

/** Whether these permissions may delete this document. Coarse fallback as for view. */
export function canDeleteAttachment(permissions: string[] | undefined, attachment: Attachment | undefined): boolean {
  const held = permissions ?? [];
  return held.includes(Permission.CASE_DOCUMENTS_DELETE)
    || held.includes(deletePermissionFor(isGenerated(attachment)));
}

/**
 * Whether these permissions may change this document — rename it, or fill in its form fields.
 *
 * No coarse fallback, deliberately: edit is a new capability, so there is nothing to inherit from.
 * Treating `case:documents:view` as implying edit would show rename affordances to every group that
 * already existed, and every one of them would 403 on save.
 */
export function canEditAttachment(permissions: string[] | undefined, attachment: Attachment | undefined): boolean {
  return (permissions ?? []).includes(editPermissionFor(isGenerated(attachment)));
}
